import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import PageBackground from '../components/PageBackground';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGUAGE_OPTIONS } from '../translations';
import { User, Camera, Globe, LogOut, Check, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_AVATARS = [
  "racer1.svg", "racer2.svg", "racer3.svg",
  "racer4.svg", "racer5.svg", "racer6.svg",
  "helmet1.svg", "helmet2.svg", "helmet3.svg",
  "helmet4.svg", "helmet5.svg", "helmet6.svg",
];

export default function Settings() {
  const navigate = useNavigate();
  const { lang, setLanguage, t } = useLanguage();
  const [user, setUser] = useState(null);
  const [name, setName] = useState('');
  const [selectedLang, setSelectedLang] = useState(lang);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/user/profile`, { withCredentials: true })
      .then(res => {
        setUser(res.data);
        setName(res.data.name || '');
        setSelectedAvatar(res.data.picture || '');
        if (res.data.language) setSelectedLang(res.data.language);
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (name.trim() && name.trim() !== user.name) params.set('name', name.trim());
      if (selectedAvatar && selectedAvatar !== user.picture) params.set('picture', selectedAvatar);
      if (selectedLang !== (user.language || 'en')) params.set('language', selectedLang);

      if (params.toString()) {
        const res = await axios.put(`${BACKEND_URL}/api/user/profile?${params}`, {}, { withCredentials: true });
        setUser(res.data);
      }
      setLanguage(selectedLang);
      toast.success(t('success'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/user/upload-avatar`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedAvatar(res.data.url);
      setUser(prev => ({ ...prev, picture: res.data.url }));
      toast.success(t('success'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      sessionStorage.removeItem('msf_rules_shown');
      toast.success(t('logout'));
      navigate('/login');
    } catch {
      toast.error(t('error'));
    }
  };

  const avatarSrc = selectedAvatar
    ? (selectedAvatar.startsWith('http') ? selectedAvatar : `${BACKEND_URL}${selectedAvatar}`)
    : null;

  if (!user) return null;

  return (
    <PageBackground pageKey="settings">
    <div className="min-h-screen">
      <Navbar user={user} />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="font-heading font-black text-3xl text-white mb-8" data-testid="settings-title">
          {t('settings')}
        </h1>

        {/* Profile Section */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 mb-6">
          <h2 className="font-heading font-bold text-lg text-primary mb-5 flex items-center gap-2">
            <User size={20} /> {t('account')}
          </h2>

          {/* Avatar */}
          <div className="flex items-center gap-6 mb-6">
            <div className="relative group">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center">
                {avatarSrc ? (
                  <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" data-testid="settings-avatar-img" />
                ) : (
                  <User size={36} className="text-zinc-500" />
                )}
              </div>
              <button
                onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                data-testid="settings-change-avatar-btn"
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              >
                <Camera size={14} className="text-white" />
              </button>
            </div>
            <div>
              <p className="text-white font-bold">{user.name}</p>
              <p className="text-zinc-500 text-sm">{user.email}</p>
            </div>
          </div>

          {/* Avatar Picker */}
          {showAvatarPicker && (
            <div className="bg-zinc-950 border border-zinc-800 p-4 mb-6" data-testid="avatar-picker">
              <p className="text-sm text-zinc-400 mb-3 font-heading">{t('choose_avatar')}</p>
              <div className="grid grid-cols-6 gap-3 mb-4">
                {PRESET_AVATARS.map(a => {
                  const url = `/api/uploads/avatars/${a}`;
                  const isSelected = selectedAvatar === url;
                  return (
                    <button
                      key={a}
                      onClick={() => { setSelectedAvatar(url); setShowAvatarPicker(false); }}
                      data-testid={`avatar-preset-${a.replace('.svg','')}`}
                      className={`w-full aspect-square rounded-full overflow-hidden border-2 transition-all hover:scale-110 ${
                        isSelected ? 'border-primary ring-2 ring-primary/50' : 'border-zinc-700'
                      }`}
                    >
                      <img src={`${BACKEND_URL}${url}`} alt={a} className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="settings-upload-avatar-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm transition-colors"
                >
                  <Upload size={14} /> {uploading ? t('loading') : t('upload_photo')}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </div>
            </div>
          )}

          {/* Display Name */}
          <div className="mb-4">
            <label className="block text-sm text-zinc-400 mb-1 font-heading">{t('display_name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="settings-name-input"
              className="w-full bg-zinc-950 border border-zinc-800 h-10 px-3 text-white focus:border-primary outline-none"
            />
          </div>
        </div>

        {/* Language Section */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6 mb-6">
          <h2 className="font-heading font-bold text-lg text-primary mb-5 flex items-center gap-2">
            <Globe size={20} /> {t('language')}
          </h2>
          <div className="grid grid-cols-1 gap-2">
            {LANGUAGE_OPTIONS.map(opt => (
              <button
                key={opt.code}
                onClick={() => setSelectedLang(opt.code)}
                data-testid={`lang-option-${opt.code}`}
                className={`flex items-center justify-between px-4 py-3 border transition-all text-left ${
                  selectedLang === opt.code
                    ? 'border-primary bg-primary/10 text-white'
                    : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                <div>
                  <span className="font-bold">{opt.nativeLabel}</span>
                  <span className="text-sm text-zinc-500 ml-2">{opt.label}</span>
                </div>
                {selectedLang === opt.code && <Check size={18} className="text-primary" />}
              </button>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="settings-save-btn"
          className="w-full py-3 bg-primary text-white font-heading font-bold uppercase text-sm hover:opacity-80 transition-all disabled:opacity-50 mb-4"
        >
          {saving ? t('loading') : t('save_changes')}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          data-testid="settings-logout-btn"
          className="w-full py-3 bg-zinc-900 border border-red-900/50 text-red-400 font-heading font-bold uppercase text-sm hover:bg-red-950/30 transition-all flex items-center justify-center gap-2"
        >
          <LogOut size={16} /> {t('logout')}
        </button>
      </div>
    </div>
    </PageBackground>
  );
}
